import React, { useCallback, useEffect, useState } from 'react';
import { rolsService, Rol, Usuari, UsuariForm, usuarisService } from '../services/api';
import ConfirmDialog from '../components/ConfirmDialog';
import UsuariModal from '../components/UsuariModal';

type FiltreRol = 'tots' | 'administradors' | 'professorat' | 'alumnat' | 'carrecs';

function UsuarisPage() {
  const [usuaris, setUsuaris] = useState<Usuari[]>([]);
  const [rols, setRols] = useState<Rol[]>([]);
  const [filtreRol, setFiltreRol] = useState<FiltreRol>('tots');
  const [initialRolIds, setInitialRolIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [usuariToEdit, setUsuariToEdit] = useState<Usuari | null>(null);
  const [usuariToDelete, setUsuariToDelete] = useState<Usuari | null>(null);

  const carregarDades = useCallback(async () => {
    try {
      setLoading(true);
      const [usuarisData, rolsData] = await Promise.all([
        usuarisService.getAll(),
        rolsService.getAll(),
      ]);
      setUsuaris(usuarisData);
      setRols(rolsData);
    } catch (error) {
      console.error('Error carregant usuaris:', error);
      alert('Error en carregar la gestio d usuaris.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarDades();
  }, [carregarDades]);

  const getRolId = (codi: string) => rols.find((rol) => rol.codi === codi)?.id;

  const getInitialRolIdsForFilter = (filtre: FiltreRol) => {
    const professorId = getRolId('PROFESSOR');

    if (filtre === 'administradors') return [getRolId('ADMINISTRADOR')].filter(Boolean) as number[];
    if (filtre === 'professorat') return [professorId].filter(Boolean) as number[];
    if (filtre === 'alumnat') return [getRolId('ALUMNE')].filter(Boolean) as number[];
    if (filtre === 'carrecs') return [professorId].filter(Boolean) as number[];
    return [];
  };

  const handleCreate = (filtre: FiltreRol = filtreRol) => {
    setUsuariToEdit(null);
    setInitialRolIds(getInitialRolIdsForFilter(filtre));
    setModalOpen(true);
  };

  const handleEdit = (usuari: Usuari) => {
    setUsuariToEdit(usuari);
    setInitialRolIds([]);
    setModalOpen(true);
  };

  const handleSave = async (data: UsuariForm) => {
    try {
      if (usuariToEdit) {
        await usuarisService.update(usuariToEdit.id, data);
      } else {
        await usuarisService.create(data);
      }

      setModalOpen(false);
      setUsuariToEdit(null);
      setInitialRolIds([]);
      await carregarDades();
    } catch (error: any) {
      console.error('Error guardant usuari:', error);
      alert(error?.response?.data?.error || 'Error en guardar l usuari.');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!usuariToDelete) return;

    try {
      await usuarisService.delete(usuariToDelete.id);
      setUsuariToDelete(null);
      await carregarDades();
    } catch (error) {
      console.error('Error eliminant usuari:', error);
      alert('Error en eliminar l usuari.');
    }
  };

  if (loading) {
    return <div className="page-container"><div className="loading">Carregant...</div></div>;
  }

  const teRol = (usuari: Usuari, codi: string) => usuari.rols.some((rol) => rol.codi === codi);
  const teCarrec = (usuari: Usuari) => usuari.rols.some((rol) => rol.categoria === 'carrec');

  const usuarisFiltrats = usuaris.filter((usuari) => {
    if (filtreRol === 'administradors') return teRol(usuari, 'ADMINISTRADOR');
    if (filtreRol === 'professorat') return teRol(usuari, 'PROFESSOR');
    if (filtreRol === 'alumnat') return teRol(usuari, 'ALUMNE');
    if (filtreRol === 'carrecs') return teCarrec(usuari);
    return true;
  });

  const comptadors = {
    tots: usuaris.length,
    administradors: usuaris.filter((usuari) => teRol(usuari, 'ADMINISTRADOR')).length,
    professorat: usuaris.filter((usuari) => teRol(usuari, 'PROFESSOR')).length,
    alumnat: usuaris.filter((usuari) => teRol(usuari, 'ALUMNE')).length,
    carrecs: usuaris.filter(teCarrec).length,
  };

  const filtres: Array<{ id: FiltreRol; label: string }> = [
    { id: 'tots', label: 'Tots' },
    { id: 'administradors', label: 'Administradors' },
    { id: 'professorat', label: 'Professorat' },
    { id: 'alumnat', label: 'Alumnat' },
    { id: 'carrecs', label: 'Carrecs' },
  ];

  return (
    <div className="page-container">
      <div className="table-container">
        <div className="table-header">
          <div>
            <h2>Gestio d usuaris</h2>
            <p className="table-subtitle">Administradors, professorat, alumnat i carrecs del centre</p>
          </div>
          <button className="btn btn-primary" onClick={() => handleCreate()}>
            + Nou usuari
          </button>
        </div>

        <div className="user-management-toolbar">
          <div className="segmented-control" role="tablist" aria-label="Filtrar usuaris per rol">
            {filtres.map((filtre) => (
              <button
                key={filtre.id}
                type="button"
                className={`segment-button${filtreRol === filtre.id ? ' active' : ''}`}
                onClick={() => setFiltreRol(filtre.id)}
              >
                {filtre.label}
                <span>{comptadors[filtre.id]}</span>
              </button>
            ))}
          </div>
          {filtreRol !== 'tots' && (
            <button className="btn btn-secondary btn-sm" onClick={() => handleCreate(filtreRol)}>
              Nou en aquesta vista
            </button>
          )}
        </div>

        {usuaris.length === 0 ? (
          <div className="empty-state">
            <p>No hi ha usuaris. Crea el primer usuari del sistema.</p>
            <button className="btn btn-primary empty-state-action" onClick={() => handleCreate()}>
              Crear usuari
            </button>
          </div>
        ) : usuarisFiltrats.length === 0 ? (
          <div className="empty-state">
            <p>No hi ha usuaris en aquest tipus de rol.</p>
            <button className="btn btn-primary empty-state-action" onClick={() => handleCreate(filtreRol)}>
              Crear usuari
            </button>
          </div>
        ) : (
          <div className="table-scroll">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Usuari</th>
                  <th>Email</th>
                  <th>Rols</th>
                  <th>Estat</th>
                  <th>Accions</th>
                </tr>
              </thead>
              <tbody>
                {usuarisFiltrats.map((usuari) => (
                  <tr key={usuari.id}>
                    <td>
                      <strong>{usuari.nom} {usuari.cognoms}</strong>
                      {usuari.telefon && <span className="text-muted user-phone">{usuari.telefon}</span>}
                    </td>
                    <td>{usuari.email}</td>
                    <td>
                      <div className="role-badges">
                        {usuari.rols.map((rol) => (
                          <span key={rol.id} className={`role-badge ${rol.categoria}`}>
                            {rol.nom}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge ${usuari.actiu ? 'active' : 'inactive'}`}>
                        {usuari.actiu ? 'Actiu' : 'Inactiu'}
                      </span>
                    </td>
                    <td>
                      <div className="actions-cell">
                        <button
                          className="btn-icon edit"
                          title="Editar"
                          onClick={() => handleEdit(usuari)}
                        >
                          Editar
                        </button>
                        <button
                          className="btn-icon delete"
                          title="Eliminar"
                          onClick={() => setUsuariToDelete(usuari)}
                        >
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <UsuariModal
        isOpen={modalOpen}
        usuari={usuariToEdit}
        rols={rols}
        initialRolIds={initialRolIds}
        onClose={() => { setModalOpen(false); setUsuariToEdit(null); setInitialRolIds([]); }}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={!!usuariToDelete}
        title="Eliminar usuari"
        message={`Estas segur que vols eliminar l usuari "${usuariToDelete?.nom} ${usuariToDelete?.cognoms}"? Aquesta accio no es pot desfer.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setUsuariToDelete(null)}
      />
    </div>
  );
}

export default UsuarisPage;
